import { useCallback, useEffect, useState } from "react"

import { useI18n } from "@/i18n"
import { getHelp } from "@/lib/api"
import type { HelpContent } from "@/types/api"

const SEEN_KEY = "celver-copilot:help-seen"

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY)
  } catch {
    return null
  }
}

/** Loads the guide once and tracks whether the user has opened the newest "What's new" entry. */
export function useHelp() {
  const { lang } = useI18n()
  const [content, setContent] = useState<HelpContent | null>(null)
  const [seen, setSeen] = useState<string | null>(readSeen)

  useEffect(() => {
    let cancelled = false
    getHelp(lang)
      .then((result) => {
        if (!cancelled) setContent(result)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [lang])

  const markSeen = useCallback(() => {
    if (!content) return
    setSeen(content.version)
    try {
      localStorage.setItem(SEEN_KEY, content.version)
    } catch {
      // the dot reappears next visit; nothing else depends on it
    }
  }, [content])

  return { content, unseen: Boolean(content?.version) && seen !== content?.version, markSeen }
}
